import type { ReactNode } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useLabelPlacement } from './labelPlacement';
import { describedBy, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/** One choice. `value` keeps its type (number, string or boolean) end to end. */
export interface RadioOption<T> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
  /** The input's id, for callers whose tests or CSS name an option by id. */
  id?: string;
}

/**
 * A group of radio buttons drawn by MUI, so they look the same in light, dark
 * and classic, and in every browser. Native `<input type="radio">` takes its
 * look from the browser and the page's `color-scheme`, which is how a light
 * page ended up with black, unselected radios (2026-10-22).
 *
 * The buttons are still real radio inputs sharing one name: Tab enters the
 * group on the selected option, the arrow keys move and select, Space selects.
 *
 * Label: `label` is shown above the options (beside them with
 * labelPlacement 'left'), and names the group. 'border' does not apply to a
 * group with no outline and reads as 'above', as checkboxes ignore it. With
 * no `label`, `ariaLabel` names the group; the caption is then usually the
 * section it sits in.
 *
 * Without a label, helper or error text the root element IS the radiogroup
 * (className and groupClassName both land on it), with each option's <label>
 * a direct child, so layout CSS written against a plain row of labels
 * (`.row > label:nth-of-type(2)`) keeps working.
 *
 * onValueChanged fires only when a person picks an option, never when `value`
 * changes. Read-only and disabled groups do not change.
 */
export interface RadioGroupFieldProps<T extends string | number | boolean> extends Pick<BaseFieldProps,
  'label' | 'labelPlacement' | 'ariaLabel' | 'disabled' | 'readOnly' | 'required' | 'error' | 'isValid' | 'errorText' | 'helperText' | 'className'> {
  value: T | null | undefined;
  options: readonly RadioOption<T>[];
  /** The picked option's value. User changes only. */
  onValueChanged: (value: T) => void;
  /** 'row' (default) wraps the options across; 'column' stacks them. */
  direction?: 'row' | 'column';
  /** The inputs' shared name. Default: generated. */
  name?: string;
  /** Class for the radiogroup element itself (the same element as the root when there is nothing else to render). */
  groupClassName?: string;
  /** Class for each option's <label>; `--checked` is appended for the picked one. */
  optionClassName?: string;
}

export default function RadioGroupField<T extends string | number | boolean>(props: RadioGroupFieldProps<T>) {
  const {
    value, options, onValueChanged, direction = 'row', name, label, ariaLabel, disabled, readOnly, required,
    helperText, errorText, className, groupClassName, optionClassName,
  } = props;
  const ids = useFieldIds();
  const placement = useLabelPlacement(props.labelPlacement);
  const invalid = isInvalid(props);
  const labelId = `${ids.id}-label`;
  // MUI compares option values as strings; the index keeps 1 and '1', or
  // false and 'false', apart and hands back the option's own value.
  const selected = options.findIndex((o) => o.value === value);
  const framed = !!(label || helperText || errorText);

  const group = (
    <RadioGroup
      row={direction === 'row'}
      name={name}
      value={selected >= 0 ? String(selected) : ''}
      onChange={(e) => {
        if (readOnly || disabled) return;
        const opt = options[Number(e.target.value)];
        if (opt) onValueChanged(opt.value);
      }}
      className={[
        'app-radio-group',
        `app-radio-group--${direction}`,
        invalid && 'app-radio-group--invalid',
        groupClassName,
        !framed && className,
      ].filter(Boolean).join(' ')}
      aria-labelledby={label ? labelId : undefined}
      aria-label={label ? undefined : ariaLabel}
      aria-describedby={describedBy(ids, { helperText, errorText })}
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
      aria-readonly={readOnly || undefined}
    >
      {options.map((opt, i) => {
        const checked = i === selected;
        return (
          <FormControlLabel
            key={i}
            value={String(i)}
            disabled={disabled || opt.disabled}
            className={optionClassName ? `${optionClassName}${checked ? ` ${optionClassName}--checked` : ''}` : undefined}
            control={<Radio size="small" id={opt.id} />}
            label={opt.label}
          />
        );
      })}
    </RadioGroup>
  );

  if (!framed) return group;

  const side = placement === 'left';
  return (
    <div className={['app-radio-field', side && 'app-radio-field--left', invalid && 'app-radio-field--invalid', disabled && 'app-field--disabled', className].filter(Boolean).join(' ')}>
      {label && (
        <div id={labelId} className="app-field-label app-radio-field-label">
          {label}
          {required && <span className="app-field-required" aria-hidden="true">*</span>}
        </div>
      )}
      <div className="app-radio-field-body">
        {group}
        {errorText && <div id={ids.errorId} className="app-field-error">{errorText}</div>}
        {helperText && <div id={ids.helperId} className="app-field-helper">{helperText}</div>}
      </div>
    </div>
  );
}
